import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, performanceBenefits } from '../data/mock';
import { ArrowRight, ArrowUpRight, Flame, Shield, Truck, Clock, ChevronRight, Target, Zap, Heart, Activity, Play, CheckCircle2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const HomePage = () => {
  const { addToCart } = useCart();
  const featuredProducts = products.slice(0, 4);

  useEffect(() => {
    document.title = 'Active Onyx Fit | Premium Boxing, MMA & Combat Sports Gear';
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#161616]">
      <Header />
      <CartDrawer />
      
      <main>
        {/* Hero Section - Split Diagonal Design */}
        <section className="hero-dark relative min-h-screen flex items-center overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-lime-950/30" />
            <div className="absolute top-0 right-0 w-1/2 h-full">
              <img 
                src={siteImages.heroMain}
                alt="Athletic training"
                className="w-full h-full object-cover opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
            </div>
          </div>
          
          {/* Animated Grid Lines */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(90deg, rgba(163,230,53,0.1) 1px, transparent 1px), linear-gradient(rgba(163,230,53,0.1) 1px, transparent 1px)',
              backgroundSize: '80px 80px'
            }} />
          </div>

          {/* Glowing Orb */}
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-lime-500/20 rounded-full blur-[200px]" />
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-40 pb-20">
            <div className="max-w-2xl">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-lime-400/10 border border-lime-400/30 rounded-full mb-8">
                <Flame className="w-4 h-4 text-lime-400" />
                <span className="text-lime-400 text-sm font-semibold tracking-wide">PRO COMBAT GEAR</span>
              </div>
              
              {/* Main Headline */}
              <h1 className="text-6xl sm:text-7xl lg:text-8xl font-black leading-[0.9] mb-8 tracking-tight">
                GLOVE
                <span className="block text-lime-400">UP.</span>
                STEP IN.
              </h1>
              
              <p className="text-xl text-white/60 leading-relaxed mb-10 max-w-lg">
                Premium boxing, MMA and combat-sports gear engineered for fighters who train like pros. Built tough. Fits clean. Wears hard.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mb-16">
                <Link 
                  to="/shop"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-lime-400 hover:bg-lime-300 text-black font-bold uppercase tracking-wider rounded-xl transition-all"
                >
                  Shop Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/why-choose-us"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/20 text-white font-bold uppercase tracking-wider rounded-xl transition-all"
                >
                  Learn More
                  <ArrowUpRight className="w-5 h-5" />
                </Link>
              </div>
              
              {/* Stats */}
              <div className="flex gap-12">
                {[
                  { value: '58+', label: 'Products' },
                  { value: '$75', label: 'Free Ship' },
                  { value: '30d', label: 'Returns' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-4xl font-black text-lime-400">{stat.value}</p>
                    <p className="text-sm text-white/40 uppercase tracking-wider mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs text-white/40 uppercase tracking-widest">Scroll</span>
            <div className="w-px h-12 bg-gradient-to-b from-lime-400 to-transparent" />
          </div>
        </section>

        {/* Features Bar - Horizontal Scroll */}
        <section className="relative bg-lime-400 py-6 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-16 mx-8">
                {[
                  { icon: Truck, text: 'FREE USA SHIPPING $75+' },
                  { icon: Shield, text: 'SECURE CHECKOUT' },
                  { icon: Clock, text: '30-DAY RETURNS' },
                  { icon: Flame, text: 'PRO-TESTED GEAR' },
                ].map((item, j) => (
                  <div key={j} className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-black" />
                    <span className="text-black font-bold tracking-wider text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* Bento Grid Products */}
        <section className="py-24 bg-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
              <div>
                <span className="text-lime-400 text-sm font-bold uppercase tracking-widest">Featured</span>
                <h2 className="text-5xl lg:text-6xl font-black mt-2">
                  TOP <span className="text-lime-400">FIGHTS</span>
                </h2>
              </div>
              <Link 
                to="/shop"
                className="group flex items-center gap-2 text-white/60 hover:text-lime-400 font-medium transition-colors"
              >
                View All
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            
            {/* Bento Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[200px]">
              {/* Large Featured Product */}
              <Link 
                to={`/product/${featuredProducts[0]?.slug}`}
                className="keep-dark col-span-2 row-span-2 group relative bg-gray-900 rounded-3xl overflow-hidden border border-gray-200 hover:border-lime-400/50 transition-all"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10" />
                <img 
                  src={featuredProducts[0]?.image}
                  alt={featuredProducts[0]?.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                  <span className="inline-block px-3 py-1 bg-lime-400 text-black text-xs font-bold rounded-full mb-3">
                    {featuredProducts[0]?.badge}
                  </span>
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-lime-400 transition-colors">
                    {featuredProducts[0]?.name}
                  </h3>
                  <p className="text-3xl font-black text-lime-400">${featuredProducts[0]?.price}</p>
                </div>
              </Link>
              
              {/* Smaller Products */}
              {featuredProducts.slice(1).map((product, i) => (
                <Link 
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className={`keep-dark group relative bg-gray-900 rounded-3xl overflow-hidden border border-gray-200 hover:border-lime-400/50 transition-all ${
                    i === 0 ? 'col-span-2' : ''
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-10" />
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
                    <h3 className="text-sm font-bold text-white group-hover:text-lime-400 transition-colors line-clamp-1">
                      {product.name}
                    </h3>
                    <p className="text-lg font-black text-lime-400">${product.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Categories - Card Stack */}
        <section className="py-24 bg-zinc-950">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-lime-400 text-sm font-bold uppercase tracking-widest">Categories</span>
              <h2 className="text-5xl lg:text-6xl font-black mt-2">
                PICK YOUR <span className="text-lime-400">WEAPON</span>
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.slice(0, 8).map((category, i) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className="group relative h-56 rounded-2xl overflow-hidden border border-gray-200 hover:border-lime-400/80 transition-all hover:-translate-y-2 hover:shadow-xl"
                >
                  {/* Category Image */}
                  {category.image && (
                    <img
                      src={category.image}
                      alt={category.name}
                      className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                  {/* Number Badge */}
                  <span className="absolute top-4 right-4 text-5xl font-black text-white/30 group-hover:text-lime-400/80 transition-colors">
                    0{i + 1}
                  </span>
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    <h3 className="text-lg font-bold text-white group-hover:text-lime-400 transition-colors">
                      {category.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-white/70 group-hover:text-lime-400 transition-colors">
                      <span className="text-sm">{category.productCount} Product{category.productCount > 1 ? 's' : ''}</span>
                      <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Performance Benefits - Split Section */}
        <section className="py-24 bg-black relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, lime 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left - Image */}
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden">
                  <img 
                    src={siteImages.powerBackground}
                    alt="Performance"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Floating Stats Card */}
                <div className="absolute -bottom-8 -right-8 bg-lime-400 text-black p-6 rounded-2xl">
                  <p className="text-5xl font-black">100%</p>
                  <p className="text-sm font-bold uppercase tracking-wider">Quality Tested</p>
                </div>
              </div>
              
              {/* Right - Content */}
              <div>
                <span className="text-lime-400 text-sm font-bold uppercase tracking-widest">Why Active Onyx Fit</span>
                <h2 className="text-5xl font-black mt-2 mb-8">
                  BUILT FOR <span className="text-lime-400">FIGHTERS</span>
                </h2>
                
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { icon: Flame, title: 'Power', desc: 'Punch heavier with gloves built for impact' },
                    { icon: Activity, title: 'Speed', desc: 'Lighter gear engineered for hand speed' },
                    { icon: Heart, title: 'Durability', desc: 'Survives daily training, round after round' },
                    { icon: Target, title: 'Style', desc: 'Combat apparel that wears as hard as it trains' },
                  ].map((item, i) => (
                    <div key={i} className="group">
                      <div className="w-14 h-14 bg-white/5 group-hover:bg-lime-400/20 border border-white/10 group-hover:border-lime-400/50 rounded-xl flex items-center justify-center mb-4 transition-all">
                        <item.icon className="w-6 h-6 text-lime-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-white/50">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Timeline */}
        <section className="py-24 bg-zinc-950">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-lime-400 text-sm font-bold uppercase tracking-widest">How It Works</span>
              <h2 className="text-5xl font-black mt-2">
                4 SIMPLE <span className="text-lime-400">STEPS</span>
              </h2>
            </div>
            
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-lime-400/50 to-transparent" />
              
              <div className="grid md:grid-cols-4 gap-8">
                {[
                  { num: '01', title: 'Browse', desc: 'Explore our combat-sports roster' },
                  { num: '02', title: 'Pick', desc: 'Choose your gloves, tees or apparel' },
                  { num: '03', title: 'Checkout', desc: 'Secure, fast, encrypted payment' },
                  { num: '04', title: 'Train', desc: 'Glove up and go to work' },
                ].map((step, i) => (
                  <div key={i} className="relative text-center group">
                    <div className="w-20 h-20 bg-black border-2 border-lime-400 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-lime-400 transition-colors">
                      <span className="text-2xl font-black text-lime-400 group-hover:text-black transition-colors">
                        {step.num}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-sm text-white/50">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Bold */}
        <section className="relative py-32 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-lime-400" />
          <div className="absolute inset-0 opacity-20">
            <img 
              src={siteImages.ctaBackground}
              alt="Background"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-5xl lg:text-7xl font-black text-black mb-6 leading-tight">
              GLOVE UP.
              <br />
              GO TO WORK.
            </h2>
            <p className="text-xl text-black/70 mb-10 max-w-xl mx-auto">
              Join the fighters and lifestyle athletes building their game in Active Onyx Fit gear.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-3 px-10 py-5 bg-black hover:bg-zinc-900 text-lime-400 font-bold uppercase tracking-wider rounded-xl transition-all text-lg"
            >
              Shop Now
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="keep-dark py-16 bg-black border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: 'USA', label: 'Based & Shipped' },
                { value: 'SSL', label: 'Secure Payments' },
                { value: '24/7', label: 'Support Ready' },
                { value: '100%', label: 'Satisfaction' },
              ].map((item, i) => (
                <div key={i}>
                  <p className="text-3xl font-black text-lime-400">{item.value}</p>
                  <p className="text-sm text-white/40 uppercase tracking-wider mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      
      {/* Custom Styles for Marquee Animation */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
